/**
 * StripeSection — Batch #6 (Zone 3 dans Paramètres Admin)
 *
 * Configure la répartition des fonds Stripe pour 2 scénarios :
 *   1. Paiement Loyer (PAD ou Carte) : [Loyer brut] + [Frais Stripe estimés] + [Commission Cabinet]
 *   2. Enquête de Crédit (Carte seulement) : [Coût fournisseur] + [Commission Cabinet] = [Montant facturé client]
 *
 * Batch #14.253 — compte Stripe de production uniquement (plus de mode test).
 */
import { COMBINED_RATE } from "@/lib/taxes";
import { useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { CreditCard, Loader2, Save, AlertTriangle, Banknote, ShieldCheck } from "lucide-react";

const PCT_OR_FIX = [
  { value: "percentage", label: "%" },
  { value: "fixed", label: "$ (fixe)" },
];

const DEFAULT_CFG = {
  mode: "live",
  rent_collection: {
    fee_method: "percentage",
    stripe_fee_percentage: 2.9,
    stripe_fee_fixed: 0.30,
    cabinet_commission_method: "percentage",
    cabinet_commission_percentage: 3.0,
    cabinet_commission_fixed: 0.00,
  },
  credit_check: {
    supplier_cost: 25.00,
    cabinet_commission_method: "fixed",
    cabinet_commission_percentage: 0.0,
    cabinet_commission_fixed: 10.00,
    client_price_total: 35.00,
  },
  // Batch #14.144 — Tarif bail signature (côté portail propriétaire uniquement)
  bail_signature: {
    free_per_cabinet: 1,
    client_price_total: 10.00,
    include_taxes: true,
    tps_rate: 5.0,
    tvq_rate: 9.975,
  },
};

export default function StripeSection() {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);  // état réel côté serveur (clé live chargée ?)

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/settings/stripe-config");
        if (mounted && r.data?.config) setCfg(r.data.config);
        const sr = await api.get("/stripe/status");
        if (mounted) setStatus(sr.data || null);
      } catch {
        toast.error("Impossible de charger la configuration Stripe");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/stripe-config", cfg);
      if (r.data?.config) setCfg(r.data.config);
      toast.success("Configuration Stripe enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  // Simulation Loyer (basé sur 1000$ exemple)
  const exampleRent = 1000;
  const stripeFee = cfg.rent_collection.fee_method === "percentage"
    ? (exampleRent * cfg.rent_collection.stripe_fee_percentage / 100) + cfg.rent_collection.stripe_fee_fixed
    : cfg.rent_collection.stripe_fee_fixed;
  const cabinetCom = cfg.rent_collection.cabinet_commission_method === "percentage"
    ? (exampleRent * cfg.rent_collection.cabinet_commission_percentage / 100)
    : cfg.rent_collection.cabinet_commission_fixed;
  const ownerNet = exampleRent - stripeFee - cabinetCom;
  const ready = !!status?.ready;

  return (
    <div className="space-y-5" data-testid="settings-stripe-section">
      {/* Batch #14.253 — Compte Stripe de production uniquement (plus de mode test ni de toggle) */}
      <div className="bg-white border rounded-xl p-4 flex items-center justify-between gap-3" data-testid="stripe-live-account-card">
        <div>
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Compte Stripe — production
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Toutes les transactions (loyers PAD/carte, enquêtes de crédit, baux, factures) passent par votre compte Stripe réel.
            {status?.secret_key_suffix && <> Clé chargée : <code className="font-mono">…{status.secret_key_suffix}</code>.</>}
            {status && !status.webhook_configured && <span className="text-amber-700"> Webhook non configuré (STRIPE_LIVE_WEBHOOK_SECRET).</span>}
          </p>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${ready ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200"}`} data-testid="stripe-live-status-badge">
          {ready ? "LIVE · connecté" : "Clé manquante"}
        </span>
      </div>

      {!ready && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2 text-xs text-rose-900" data-testid="stripe-missing-key-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>Aucune clé Stripe de production détectée côté serveur. Renseignez <code>STRIPE_LIVE_SECRET_KEY</code>, <code>STRIPE_LIVE_PUBLIC_KEY</code> et <code>STRIPE_LIVE_WEBHOOK_SECRET</code> dans les variables d'environnement du backend, puis redémarrez.</div>
        </div>
      )}

      {/* Scenario 1 — Paiement Loyer */}
      <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="stripe-rent-config">
        <div className="flex items-center gap-2 border-b pb-2">
          <Banknote className="w-4 h-4 text-emerald-600" />
          <h4 className="font-semibold text-sm">Paiement Loyer (PAD ou Carte de crédit)</h4>
        </div>

        {/* Frais Stripe */}
        <div className="grid grid-cols-3 gap-3 items-end">
          <Field label="Méthode des frais Stripe">
            <select
              value={cfg.rent_collection.fee_method}
              onChange={(e) => setCfg({ ...cfg, rent_collection: { ...cfg.rent_collection, fee_method: e.target.value } })}
              className="cc-input"
              data-testid="stripe-rent-fee-method"
            >
              {PCT_OR_FIX.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label={cfg.rent_collection.fee_method === "percentage" ? "Frais Stripe estimés (%)" : "Frais Stripe estimés ($)"}>
            <input
              type="number"
              step="0.01"
              value={cfg.rent_collection.fee_method === "percentage" ? cfg.rent_collection.stripe_fee_percentage : cfg.rent_collection.stripe_fee_fixed}
              onChange={(e) => setCfg({
                ...cfg,
                rent_collection: cfg.rent_collection.fee_method === "percentage"
                  ? { ...cfg.rent_collection, stripe_fee_percentage: parseFloat(e.target.value) || 0 }
                  : { ...cfg.rent_collection, stripe_fee_fixed: parseFloat(e.target.value) || 0 },
              })}
              className="cc-input font-mono"
              data-testid="stripe-rent-fee-value"
            />
          </Field>
          {cfg.rent_collection.fee_method === "percentage" && (
            <Field label="+ Frais fixe ($)">
              <input
                type="number"
                step="0.01"
                value={cfg.rent_collection.stripe_fee_fixed}
                onChange={(e) => setCfg({ ...cfg, rent_collection: { ...cfg.rent_collection, stripe_fee_fixed: parseFloat(e.target.value) || 0 } })}
                className="cc-input font-mono"
                data-testid="stripe-rent-fee-fixed"
              />
            </Field>
          )}
        </div>

        {/* Commission cabinet */}
        <div className="grid grid-cols-2 gap-3 items-end">
          <Field label="Méthode de commission cabinet">
            <select
              value={cfg.rent_collection.cabinet_commission_method}
              onChange={(e) => setCfg({ ...cfg, rent_collection: { ...cfg.rent_collection, cabinet_commission_method: e.target.value } })}
              className="cc-input"
              data-testid="stripe-rent-commission-method"
            >
              {PCT_OR_FIX.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label={cfg.rent_collection.cabinet_commission_method === "percentage" ? "Commission cabinet (%)" : "Commission cabinet ($)"}>
            <input
              type="number"
              step="0.01"
              value={cfg.rent_collection.cabinet_commission_method === "percentage" ? cfg.rent_collection.cabinet_commission_percentage : cfg.rent_collection.cabinet_commission_fixed}
              onChange={(e) => setCfg({
                ...cfg,
                rent_collection: cfg.rent_collection.cabinet_commission_method === "percentage"
                  ? { ...cfg.rent_collection, cabinet_commission_percentage: parseFloat(e.target.value) || 0 }
                  : { ...cfg.rent_collection, cabinet_commission_fixed: parseFloat(e.target.value) || 0 },
              })}
              className="cc-input font-mono"
              data-testid="stripe-rent-commission-value"
            />
          </Field>
        </div>

        {/* Simulation */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1" data-testid="stripe-rent-simulation">
          <div className="font-semibold text-slate-700 uppercase text-[10px] tracking-wider mb-1">Simulation pour {exampleRent} $ de loyer</div>
          <Row label="Loyer brut" value={exampleRent} positive />
          <Row label="Frais Stripe estimés" value={-stripeFee} />
          <Row label="Commission cabinet" value={-cabinetCom} />
          <div className="border-t pt-1 mt-1 flex justify-between text-sm font-bold">
            <span className="text-slate-800">Net au propriétaire</span>
            <span className="font-mono text-emerald-700">{ownerNet.toFixed(2)} $</span>
          </div>
        </div>
      </div>

      {/* Scenario 2 — Enquête Crédit */}
      <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="stripe-credit-config">
        <div className="flex items-center gap-2 border-b pb-2">
          <ShieldCheck className="w-4 h-4 text-indigo-600" />
          <h4 className="font-semibold text-sm">Enquête de Crédit (Carte de crédit seulement)</h4>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Coût fournisseur ($)">
            <input
              type="number"
              step="0.01"
              value={cfg.credit_check.supplier_cost}
              onChange={(e) => setCfg({ ...cfg, credit_check: { ...cfg.credit_check, supplier_cost: parseFloat(e.target.value) || 0 } })}
              className="cc-input font-mono"
              data-testid="stripe-credit-supplier-cost"
            />
          </Field>
          <Field label="Méthode commission">
            <select
              value={cfg.credit_check.cabinet_commission_method}
              onChange={(e) => setCfg({ ...cfg, credit_check: { ...cfg.credit_check, cabinet_commission_method: e.target.value } })}
              className="cc-input"
              data-testid="stripe-credit-commission-method"
            >
              {PCT_OR_FIX.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label={cfg.credit_check.cabinet_commission_method === "percentage" ? "Commission (%)" : "Commission ($)"}>
            <input
              type="number"
              step="0.01"
              value={cfg.credit_check.cabinet_commission_method === "percentage" ? cfg.credit_check.cabinet_commission_percentage : cfg.credit_check.cabinet_commission_fixed}
              onChange={(e) => setCfg({
                ...cfg,
                credit_check: cfg.credit_check.cabinet_commission_method === "percentage"
                  ? { ...cfg.credit_check, cabinet_commission_percentage: parseFloat(e.target.value) || 0 }
                  : { ...cfg.credit_check, cabinet_commission_fixed: parseFloat(e.target.value) || 0 },
              })}
              className="cc-input font-mono"
              data-testid="stripe-credit-commission-value"
            />
          </Field>
        </div>

        <Field label="Montant final facturé au client ($) — avant taxes">
          <input
            type="number"
            step="0.01"
            value={cfg.credit_check.client_price_total}
            onChange={(e) => setCfg({ ...cfg, credit_check: { ...cfg.credit_check, client_price_total: parseFloat(e.target.value) || 0 } })}
            className="cc-input font-mono"
            data-testid="stripe-credit-client-price"
          />
        </Field>

        {/* Toggle taxes Batch #7 */}
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="stripe-credit-taxes-toggle">
            <input
              type="checkbox"
              checked={cfg.credit_check.include_taxes || false}
              onChange={(e) => setCfg({ ...cfg, credit_check: { ...cfg.credit_check, include_taxes: e.target.checked } })}
              className="w-4 h-4"
            />
            <span className="text-xs font-semibold text-slate-700">
              Appliquer la TPS / TVQ sur ce service
            </span>
            <span className="text-[10px] text-slate-500 italic">
              (décocher si exempt ou cabinet sans inscription)
            </span>
          </label>
          {cfg.credit_check.include_taxes && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Field label="Taux TPS (%)">
                <input
                  type="number"
                  step="0.001"
                  value={cfg.credit_check.tps_rate || 5.0}
                  onChange={(e) => setCfg({ ...cfg, credit_check: { ...cfg.credit_check, tps_rate: parseFloat(e.target.value) || 0 } })}
                  className="cc-input font-mono"
                  data-testid="stripe-credit-tps-rate"
                />
              </Field>
              <Field label="Taux TVQ (%)">
                <input
                  type="number"
                  step="0.001"
                  value={cfg.credit_check.tvq_rate || 9.975}
                  onChange={(e) => setCfg({ ...cfg, credit_check: { ...cfg.credit_check, tvq_rate: parseFloat(e.target.value) || 0 } })}
                  className="cc-input font-mono"
                  data-testid="stripe-credit-tvq-rate"
                />
              </Field>
            </div>
          )}
        </div>

        {/* Simulation crédit (Batch #13.3 — Affichage corrigé : Commission = Profit net) */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1" data-testid="stripe-credit-simulation">
          <div className="font-semibold text-slate-700 uppercase text-[10px] tracking-wider mb-1">Simulation d'un dossier crédit</div>
          <Row label="Montant facturé au locataire" value={cfg.credit_check.client_price_total} positive />
          <Row label="Coût fournisseur (Cogeco / Equifax)" value={-cfg.credit_check.supplier_cost} />
          <div className="border-t pt-1 mt-1 flex justify-between text-sm font-bold">
            <span className="text-emerald-800">Profit net du cabinet</span>
            <span className="font-mono text-emerald-700" data-testid="stripe-credit-profit-net">
              {(cfg.credit_check.client_price_total - cfg.credit_check.supplier_cost).toFixed(2)} $
            </span>
          </div>
          <div className="text-[10px] text-slate-500 italic pt-1 leading-relaxed">
            Cette marge correspond à votre <strong>commission configurée</strong> (
            {cfg.credit_check.cabinet_commission_method === "percentage"
              ? `${cfg.credit_check.cabinet_commission_percentage}% du montant facturé`
              : `${cfg.credit_check.cabinet_commission_fixed.toFixed(2)} $ fixe`}
            ). Le tarif client est automatiquement ajusté pour viser ce profit.
          </div>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────────── */}
      {/* Batch #14.144 — Bail TAL Signature (côté portail propriétaire) */}
      {/* ───────────────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="stripe-bail-section">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-6 bg-indigo-500 rounded-full"></span>
          <h3 className="font-semibold text-slate-800">Bail TAL — Signature électronique</h3>
          <span className="ml-auto text-[10px] uppercase font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
            Côté propriétaire uniquement
          </span>
        </div>
        <div className="text-xs text-slate-600 -mt-1">
          L'admin/comptable peut envoyer des baux pour signature <strong>illimité gratuit</strong>.
          Le propriétaire bénéficie de <strong>{cfg.bail_signature?.free_per_cabinet ?? 1} envoi(s) gratuit(s)</strong>,
          puis paie au-delà.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Envois gratuits par propriétaire">
            <input
              type="number"
              min="0"
              step="1"
              value={cfg.bail_signature?.free_per_cabinet ?? 1}
              onChange={(e) => setCfg({ ...cfg, bail_signature: { ...(cfg.bail_signature || {}), free_per_cabinet: parseInt(e.target.value) || 0 } })}
              className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
              data-testid="stripe-bail-free-input"
            />
          </Field>
          <Field label="Prix client après gratuité ($)">
            <input
              type="number"
              step="0.01"
              value={cfg.bail_signature?.client_price_total ?? 10.00}
              onChange={(e) => setCfg({ ...cfg, bail_signature: { ...(cfg.bail_signature || {}), client_price_total: parseFloat(e.target.value) || 0 } })}
              className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono"
              data-testid="stripe-bail-price-input"
            />
          </Field>
          <Field label="Taxes (TPS + TVQ)">
            <label className="inline-flex items-center gap-2 mt-1.5 text-sm">
              <input
                type="checkbox"
                checked={cfg.bail_signature?.include_taxes ?? true}
                onChange={(e) => setCfg({ ...cfg, bail_signature: { ...(cfg.bail_signature || {}), include_taxes: e.target.checked } })}
                className="rounded text-indigo-600"
                data-testid="stripe-bail-taxes-checkbox"
              />
              <span className="text-slate-700">Ajouter TPS 5% + TVQ 9.975%</span>
            </label>
          </Field>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs">
          <div className="font-semibold text-indigo-900 uppercase text-[10px] tracking-wider mb-1">Prix client total</div>
          <div className="font-mono text-indigo-900">
            {(cfg.bail_signature?.client_price_total ?? 10).toFixed(2)} $
            {(cfg.bail_signature?.include_taxes ?? true) && (
              <> + {((cfg.bail_signature?.client_price_total ?? 10) * COMBINED_RATE).toFixed(2)} $ (TPS+TVQ) = <strong>{((cfg.bail_signature?.client_price_total ?? 10) * (1 + COMBINED_RATE)).toFixed(2)} $</strong></>
            )}
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end gap-2">
        {cfg.updated_at && (
          <span className="text-[10px] text-slate-400 self-center">
            Dernière màj : {cfg.updated_at.slice(0, 19).replace("T", " ")} {cfg.updated_by ? `par ${cfg.updated_by}` : ""}
          </span>
        )}
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="stripe-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Enregistrer la configuration
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, value, positive = false }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono ${positive ? "text-emerald-700" : value < 0 ? "text-rose-700" : "text-slate-700"}`}>
        {value >= 0 ? "+" : ""}{value.toFixed(2)} $
      </span>
    </div>
  );
}
